/**
 * api.js — Centralized API service layer
 *
 * All backend calls go through this file.
 * Components never import axios directly — they import from here.
 *
 * Benefits:
 *  - Single place to change the base URL (env var)
 *  - Consistent error normalisation
 *  - Easy to add auth headers, retry logic, or mock in tests
 */

import axios from 'axios';
import { auth } from '../firebase';
import { inc as loaderInc, dec as loaderDec } from './loaderTracker';

// ── Base client ──────────────────────────────────────────────────────────────
export const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const client = axios.create({
    baseURL: BASE_URL,
    timeout: 30_000,
    headers: { 'Content-Type': 'application/json' },
});

// Called once on app load after Firestore infra config is fetched.
// Updates baseURL for all subsequent calls without a page reload.
export function setActiveBackendUrl(url) {
    if (url) client.defaults.baseURL = url;
}

// Intercept all requests and attach Firebase auth token
client.interceptors.request.use(async (config) => {
    if (!config.headers?.['X-Silent-Loader']) {
        loaderInc();
        config._loaderTracked = true;
    }
    try {
        const isMockAuthEnabled = String(process.env.REACT_APP_ENABLE_MOCK_AUTH).toLowerCase() === 'true';

        if (isMockAuthEnabled) {
            // Priority 1: Mock bypass for local dev
            config.headers.Authorization = 'Bearer e2e-mock-token'; // foxguard: ignore[js/no-hardcoded-secret]
        } else if (auth.currentUser) {
            // Priority 2: Real Firebase auth
            const token = await auth.currentUser.getIdToken();
            config.headers.Authorization = `Bearer ${token}`; // foxguard: ignore[js/no-hardcoded-secret]
        }
    } catch (err) {
        console.error('[API ERROR] Interceptor failed:', err);
    }
    return config;
}, (error) => {
    if (error?.config?._loaderTracked) loaderDec();
    return Promise.reject(error);
});

let isRefreshing = false;
let refreshSubscribers = [];

function onTokenRefreshed(token) {
    refreshSubscribers.forEach((cb) => cb(token));
    refreshSubscribers = [];
}

function addRefreshSubscriber(cb) {
    refreshSubscribers.push(cb);
}

client.interceptors.response.use(
    (response) => {
        if (response?.config?._loaderTracked) {
            loaderDec();
            response.config._loaderTracked = false;
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        if (originalRequest?._loaderTracked) {
            loaderDec();
            originalRequest._loaderTracked = false;
        }

        // If 401 and we haven't already retried this request
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            if (isRefreshing) {
                // Queue this request — it will be retried once the token refresh completes
                return new Promise((resolve) => {
                    addRefreshSubscriber((newToken) => {
                        originalRequest.headers.Authorization = `Bearer ${newToken}`; // foxguard: ignore[js/no-hardcoded-secret]
                        resolve(client(originalRequest));
                    });
                });
            }

            isRefreshing = true;
            try {
                if (auth.currentUser) {
                    // Force-refresh the token (ignores cache)
                    const newToken = await auth.currentUser.getIdToken(true);
                    isRefreshing = false;
                    onTokenRefreshed(newToken);
                    originalRequest.headers.Authorization = `Bearer ${newToken}`; // foxguard: ignore[js/no-hardcoded-secret]
                    return client(originalRequest);  // Retry the original request
                }
            } catch (refreshError) {
                isRefreshing = false;
                refreshSubscribers = [];
                // Token refresh failed — user needs to log in again
                console.error('Token refresh failed:', refreshError);
            }
            isRefreshing = false;
        }

        return Promise.reject(error);
    }
);

function createApiError(err) {
    const rawError = err.response?.data?.error || err.response?.data?.message || err.message || 'An unexpected error occurred.';
    const error = new Error(rawError);
    // Preserve response and status for legacy downstream UI components that
    // expect axios-like err.response.status === 429 structures.
    error.status = err.response?.status;
    error.response = err.response;
    
    if (err.response?.status === 402 || err.response?.data?.is_quota_error) {
        error.isQuotaError = true;
    }
    if (err.response?.data?.error_type) {
        error.errorType = err.response.data.error_type;
    }
    if (err.response?.data?.is_upgrade_required) {
        error.isUpgradeRequired = true;
    }
    if (err.response?.status === 403 && rawError.includes('disabled for your account')) {
        error.isFeatureDisabled = true;
    }
    return error;
}

// ── Analytics ────────────────────────────────────────────────────────────────
export const analyticsApi = {
    /** Fetch aggregated platform analytics */
    getAll: async () => {
        try {
            const { data } = await client.get('/analytics');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Fetch follower counts + history across all platforms */
    getFollowers: async (days = 30) => {
        try {
            const { data } = await client.get('/analytics/followers', { params: { days } });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Fetch per-post follower impact / ROI detail */
    getPostImpact: async (postId) => {
        try {
            const { data } = await client.get(`/analytics/post-impact/${postId}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Lead CRM ────────────────────────────────────────────────────────────────
export const leadApi = {
    getAll: async (status = null) => {
        try {
            const params = status ? { status } : {};
            const { data } = await client.get('/api/leads', { params });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    update: async (leadId, updateData) => {
        try {
            const payload = typeof updateData === 'string' ? { status: updateData } : updateData;
            const { data } = await client.put(`/api/leads/${leadId}`, payload);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    delete: async (leadId) => {
        try {
            const { data } = await client.delete(`/api/leads/${leadId}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    create: async (leadData) => {
        try {
            const { data } = await client.post('/api/leads', leadData);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    }
};

// ── Posting ──────────────────────────────────────────────────────────────────
export const postApi = {
    /**
     * Batch post to multiple platforms.
     * @param {FormData} formData  — multipart/form-data with `posts` JSON + optional media files
     */
    post: async (formData) => {
        try {
            const { data } = await client.post('/post', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 120_000,
            });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Fetch historic posts */
    getAll: async (limit = 50, platform = null) => {
        try {
            const params = { limit };
            if (platform) params.platform = platform;
            const { data } = await client.get('/api/posts', { params });
            return data.data || [];
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Media / R2 ──────────────────────────────────────────────────────────────
export const mediaApi = {
    initiateUpload: async (filename, contentType, sha256) => {
        try {
            const { data } = await client.post('/api/media/initiate-upload', { filename, contentType, sha256 });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    presignPart: async (r2Path, uploadId, partNumber) => {
        try {
            const { data } = await client.post('/api/media/presign-part', { r2Path, uploadId, partNumber });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    completeUpload: async (uploadId, r2Path, mediaId, parts) => {
        try {
            const { data } = await client.post('/api/media/complete-upload', { uploadId, r2Path, mediaId, parts });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── YouTube Direct Upload (large video path ≥ 500 MB) ────────────────────────
export const youtubeApi = {
    /**
     * Ask the backend to create a YouTube resumable upload session.
     * Returns { uploadUri } — a self-authenticated URL the browser can PUT to directly.
     */
    getResumableUri: async (payload) => {
        try {
            const { data } = await client.post('/api/youtube/resumable-uri', payload);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /**
     * Called after the browser has finished uploading directly to YouTube.
     * Saves the post to Firestore, sets the thumbnail, and emits the socket event.
     */
    completeDirectUpload: async (payload) => {
        try {
            const { data } = await client.post('/api/youtube/complete-direct-upload', payload);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Social Accounts (multi-account) ──────────────────────────────────────────
export const socialAccountsApi = {
    list: async () => {
        try {
            const { data } = await client.get('/api/social-accounts');
            return data; // { accounts, current, limit, can_add }
        } catch (err) {
            throw createApiError(err);
        }
    },
    remove: async (accountId) => {
        try {
            const { data } = await client.delete(`/api/social-accounts/${accountId}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    getLimit: async () => {
        try {
            const { data } = await client.get('/api/social-accounts/limit');
            return data; // { current, limit, can_add }
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Content Analysis ─────────────────────────────────────────────────────────
export const analyzeApi = {
    /**
     * Analyse a piece of text for sentiment, engagement, readability, etc.
     * @param {string} text
     */
    analyze: async (text, platform = null) => {
        try {
            const { data } = await client.post('/analyze', { text, ...(platform && { platform }) });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /**
     * Get AI suggestions for content optimization.
     */
    optimizeContent: async (params) => {
        try {
            const { data } = await client.post('/optimize', params, {
                timeout: 120_000
            });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /**
     * Smart Compose — adapt a single piece of content for multiple platforms at once.
     * @param {string} content - Topic, idea, or draft text
     * @param {string[]} platforms - Target platform names (e.g. ['instagram', 'youtube', 'twitter'])
     * @param {string} [tone='engaging'] - Desired tone
     * @returns {Promise<{platforms: Object, source_content: string}>}
     */
    smartCompose: async (content, platforms, tone = 'engaging') => {
        try {
            const { data } = await client.post('/smart-compose', { content, platforms, tone }, {
                timeout: 120_000
            });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /**
     * Generate an AI thumbnail image from a text prompt.
     * @param {string} prompt - Description of the desired thumbnail
     * @param {string} [platform='youtube'] - Target platform (determines aspect ratio)
     * @returns {Promise<{url: string, aspect_ratio: string, platform: string, prompt: string}>}
     */
    generateThumbnail: async (prompt, platform = 'youtube') => {
        try {
            const { data } = await client.post('/generate-thumbnail', { prompt, platform }, {
                timeout: 40_000
            });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    }
};

// ── Gap Analysis ─────────────────────────────────────────────────────────────
export const gapsApi = {
    /**
     * Full structured gap analysis.
     * @param {string} yourContent
     * @param {string} competitorContent
     * @param {object} opts — { platform, compareMode }
     */
    analyze: async (yourContent, competitorContent, opts = {}) => {
        try {
            const { data } = await client.post('/gaps', {
                current_content: yourContent,
                top_content: competitorContent,
                platform: opts.platform || null,
                compare_mode: opts.compareMode || 'competitor',
            });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    save: async (analysisSummary, yourContentPreview, compareMode, platform) => {
        try {
            const { data } = await client.post('/gaps/save', {
                analysis_summary: analysisSummary,
                your_content_preview: yourContentPreview || '',
                compare_mode: compareMode || 'competitor',
                platform: platform || '',
            });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    getSaved: async () => {
        try {
            const { data } = await client.get('/gaps/saved');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    deleteSaved: async (analysisId) => {
        try {
            const { data } = await client.delete(`/gaps/saved/${analysisId}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    getHistory: async () => {
        try {
            const { data } = await client.get('/gaps/history');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Check which optional backend features are available */
    health: async () => {
        try {
            const { data } = await client.get('/gaps/health');
            return data;
        } catch (_err) {
            // Health check failure is non-fatal — degrade gracefully
            return { url_fetch: false, ner: false };
        }
    },
};

// ── Rankings ─────────────────────────────────────────────────────────────────
export const rankApi = {
    /** Rank all posted content by engagement score (platform-level) */
    getRanked: async () => {
        try {
            const { data } = await client.get('/rank');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Deep content intelligence: top posts, best times, content types, AI insights */
    getIntelligence: async () => {
        try {
            const { data } = await client.get('/rank/intelligence');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /**
     * Generate a deep AI strategy report from intelligence data.
     * Costs 1 AI credit.
     * @param {{ platform_stats, top_posts, best_times, cross_platform }} payload
     */
    generateDeepAnalysis: async (payload) => {
        try {
            const { data } = await client.post('/rank/ai-deep-analysis', payload);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

export const ideasApi = {
    /**
     * Generate structured content ideas for a topic.
     * @param {string} topic
     * @param {string|null} platform  — optional platform filter e.g. 'Instagram'
     * @param {number} count          — number of ideas (default 5)
     * @param {string} mode           — 'smart' | 'fill_gap' | 'double_down' | 'trending_you'
     */
    generate: async (topic, platform = null, count = 5, mode = 'smart') => {
        try {
            const { data } = await client.post('/ideas', { topic, platform, count, mode });
            return data;   // { ideas: [...], topic, platform, mode, personalized }
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Bookmark/save an idea */
    save: async (idea, sourceTopic = '', sourceMode = 'smart') => {
        try {
            const { data } = await client.post('/ideas/save', { idea, source_topic: sourceTopic, source_mode: sourceMode });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** List saved ideas */
    getSaved: async (limit = 50) => {
        try {
            const { data } = await client.get('/ideas/saved', { params: { limit } });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Delete a saved idea */
    deleteSaved: async (ideaId) => {
        try {
            const { data } = await client.delete(`/ideas/saved/${ideaId}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Get idea generation history */
    getHistory: async (limit = 20) => {
        try {
            const { data } = await client.get('/ideas/history', { params: { limit } });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Credentials (encrypted via backend) ──────────────────────────────────────
export const credentialsApi = {
    /**
     * Get all platform credentials (values are masked — just shows if set or not).
     * The backend decrypts the Fernet ciphertext before responding.
     */
    getAll: async () => {
        try {
            const { data } = await client.get('/api/credentials');
            return data;  // { instagram: { access_token: '••••••••' }, ... }
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Save platform credentials (backend encrypts before writing to Firestore).
     * @param {string} platform  — e.g. 'instagram'
     * @param {Object} fields    — { field_name: 'value', ... }
     */
    save: async (platform, fields) => {
        try {
            const { data } = await client.put(`/api/credentials/${platform}`, fields);
            return data;  // { status: 'saved', platform }
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Delete all credentials for a platform.
     * @param {string} platform  — e.g. 'instagram'
     */
    delete: async (platform) => {
        try {
            const { data } = await client.delete(`/api/credentials/${platform}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Get the YouTube OAuth URL to redirect the user to.
     */
    getYouTubeAuthUrl: async () => {
        try {
            const { data } = await client.get('/api/youtube/auth');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Submit the YouTube OAuth callback code to the backend.
     */
    handleYouTubeCallback: async (code, state) => {
        try {
            const { data } = await client.post('/api/youtube/callback', { code, state });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Get the Instagram OAuth URL to redirect the user to.
     */
    getInstagramAuthUrl: async () => {
        try {
            const { data } = await client.get('/api/instagram/auth');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Exchange the Instagram OAuth code for a long-lived access token.
     */
    handleInstagramCallback: async (code, state, pageId) => {
        try {
            const { data } = await client.post('/api/instagram/callback', { code, state, ...(pageId ? { page_id: pageId } : {}) });
            return data;
        } catch (err) {
            const apiErr = createApiError(err);
            apiErr.needsPageId = err.response?.data?.needs_page_id === true;
            throw apiErr;
        }
    },
    resolveInstagramPage: async (pageId) => {
        try {
            const { data } = await client.post('/api/instagram/resolve-page', { page_id: pageId });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Get the Facebook OAuth URL to redirect the user to.
     */
    getFacebookAuthUrl: async () => {
        try {
            const { data } = await client.get('/api/facebook/auth');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Exchange the Facebook OAuth code for a permanent page access token.
     * Optionally pass page_id to select a specific page when the user manages multiple.
     */
    handleFacebookCallback: async (code, state, pageId = null) => {
        try {
            const { data } = await client.post('/api/facebook/callback', { code, state, page_id: pageId });
            return data;
        } catch (err) {
            const apiErr = createApiError(err);
            apiErr.needsPageId = err.response?.data?.needs_page_id === true;
            throw apiErr;
        }
    },
    resolveFacebookPage: async (pageId) => {
        try {
            const { data } = await client.post('/api/facebook/resolve-page', { page_id: pageId });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Billing ───────────────────────────────────────────────────────────────────
export const billingApi = {
    getConfig: async () => {
        try {
            const { data } = await client.get('/api/billing/config');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    createCheckoutSession: async (planId = 'pro', currency = 'usd', couponId = null) => {
        try {
            const { data } = await client.post('/api/billing/create-checkout-session', { planId, currency, couponId });
            return data; // { url: 'https://checkout.stripe.com/...' }
        } catch (err) {
            throw createApiError(err);
        }
    },
    createPortalSession: async () => {
        try {
            const { data } = await client.post('/api/billing/create-portal-session');
            return data; // { url: 'https://billing.stripe.com/...' }
        } catch (err) {
            throw createApiError(err);
        }
    },
    getPublicToken: async () => {
        try {
            const { data } = await client.get('/api/billing/get-public-token');
            return data.token; // The signed JWT
        } catch (err) {
            throw createApiError(err);
        }
    },
    getPublicConfig: async (token) => {
        try {
            const { data } = await client.get('/api/billing/public-config', {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            return data; // { plans: { ... } }
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Current-month usage stats for the authenticated user */
    getUsage: async () => {
        try {
            const { data } = await client.get('/api/billing/usage');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Subscription expiry / renewal info */
    getSubscriptionStatus: async () => {
        try {
            const { data } = await client.get('/api/billing/subscription-status');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    getActiveDiscounts: async () => {
        try {
            const { data } = await client.get('/api/billing/active-discounts');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Create a Razorpay subscription for an INR plan.
     * Returns { subscription_id, key_id }.
     */
    createRazorpaySubscription: async (planId) => {
        try {
            const { data } = await client.post('/api/billing/razorpay/create-subscription', { planId });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Verify a completed Razorpay payment on the server and grant the plan.
     * Sends the three values from the Razorpay checkout handler.
     * Returns { plan, payment_id }.
     */
    verifyRazorpayPayment: async (razorpay_payment_id, razorpay_subscription_id, razorpay_signature) => {
        try {
            const { data } = await client.post('/api/billing/razorpay/verify-payment', {
                razorpay_payment_id,
                razorpay_subscription_id,
                razorpay_signature,
            });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Automation ──────────────────────────────────────────────────────────────
export const automationApi = {
    /** Get AI auto-responder settings */
    getSettings: async () => {
        try {
            const { data } = await client.get('/automation/settings');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Save AI auto-responder settings */
    updateSettings: async (settings) => {
        try {
            const { data } = await client.post('/automation/settings', settings);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Get pending replies requiring manual review */
    getPendingReplies: async () => {
        try {
            const { data } = await client.get('/engagement/pending');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Approve/Send pending reply, optionally edited */
    approveReply: async (docId, text = null) => {
        try {
            const { data } = await client.post(`/engagement/approve/${docId}`, { text });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Reject and delete pending reply */
    rejectReply: async (docId) => {
        try {
            const { data } = await client.post(`/engagement/reject/${docId}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /**
     * Refresh comments for all hot/warm posts on a platform.
     * Returns { posts_scanned, posts_total, fetched, replied, queued,
     *           already_replied, skipped, failed, timed_out }
     */
    refreshComments: async (platform) => {
        try {
            const { data } = await client.post(`/engagement/refresh?platform=${platform}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /**
     * Fetch the per-comment outcome log for a single refresh batch.
     * Returns { refresh_id, entries: [{platform, post_id, comment_id, comment_text,
     *           username, status, skip_reason, error_message, processed_at}, ...] }
     */
    getRefreshLog: async (refreshId, { pageSize = 100, cursor = null } = {}) => {
        try {
            const params = { page_size: pageSize };
            if (cursor) params.cursor = cursor;
            const { data } = await client.get(
                `/engagement/refresh-log/${encodeURIComponent(refreshId)}`,
                { params }
            );
            return data; // { refresh_id, entries, has_more, next_cursor }
        } catch (err) {
            throw createApiError(err);
        }
    },
    /**
     * Re-run AI + approval pipeline for a single comment that was previously
     * dropped (toxic / no_ai / already_replied). Drops the dedup lock and
     * pushes a fresh task.
     * Returns { ok, queued, refresh_id, message? }
     */
    reprocessComment: async ({ platform, post_id, comment_id, force = false }) => {
        try {
            const { data } = await client.post('/engagement/reprocess', {
                platform, post_id, comment_id, force,
            });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /**
     * Aggregate comment processing stats for the current user.
     * Returns { total, by_status, by_skip_reason, by_platform, write_failures, since_days }
     */
    getEngagementAnalytics: async ({ sinceDays = 7 } = {}) => {
        try {
            const { data } = await client.get('/engagement/analytics', {
                params: { since_days: sinceDays },
            });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Newsletter ─────────────────────────────────────────────────────────────
export const newsletterApi = {
    // ── Public ────────────────────────────────────────────────────────────

    /** Subscribe — returns { success, requiresVerification, refLink } for new; alreadySubscribed for existing */
    subscribe: async (email, metadata = {}, ref = null) => {
        try {
            const { data } = await client.post('/newsletter/subscribe', { email, metadata, ref });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    // Note: /newsletter/verify returns a 302 redirect handled server-side;
    // the React page reads URL params set by the backend redirect directly.

    /** Save onboarding answers */
    saveOnboarding: async (email, { role, platforms, painPoint }) => {
        try {
            const { data } = await client.post('/newsletter/onboarding', { email, role, platforms, painPoint });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Fetch active subscriber count for social proof */
    getCount: async () => {
        try {
            const { data } = await client.get('/newsletter/count');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** GET /newsletter/preferences?token=X */
    getPreferences: async (token) => {
        try {
            const { data } = await client.get('/newsletter/preferences', { params: { token } });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** POST /newsletter/preferences?token=X */
    updatePreferences: async (token, { role, platforms, painPoint }) => {
        try {
            const { data } = await client.post('/newsletter/preferences', { role, platforms, painPoint }, { params: { token } });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** DELETE /newsletter/me?token=X — GDPR erasure */
    deleteMyData: async (token) => {
        try {
            const { data } = await client.delete('/newsletter/me', { params: { token } });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    // ── Admin (super_admin only) ───────────────────────────────────────────

    /** GET /admin/newsletter/subscribers — paginated with optional filters */
    getSubscribers: async ({ segment, source, status = 'active', page = 1, perPage = 50 } = {}) => {
        try {
            const params = { status, page, per_page: perPage };
            if (segment) params.segment = segment;
            if (source)  params.source  = source;
            const { data } = await client.get('/admin/newsletter/subscribers', { params });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** DELETE /admin/newsletter/subscriber/:id */
    deleteSubscriber: async (subscriberId) => {
        try {
            const { data } = await client.delete(`/admin/newsletter/subscriber/${subscriberId}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** POST /admin/newsletter/subscriber/:id/resend-welcome */
    resendWelcome: async (subscriberId) => {
        try {
            const { data } = await client.post(`/admin/newsletter/subscriber/${subscriberId}/resend-welcome`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** GET /admin/newsletter/stats */
    getStats: async () => {
        try {
            const { data } = await client.get('/admin/newsletter/stats');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** POST /admin/newsletter/broadcast — supports scheduled_at (ISO-8601 UTC) */
    broadcast: async ({ subject, bodyHtml, bodyText = '', segment = null, scheduledAt = null }) => {
        try {
            const { data } = await client.post('/admin/newsletter/broadcast', {
                subject,
                body_html:    bodyHtml,
                body_text:    bodyText,
                segment,
                scheduled_at: scheduledAt,
            });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** GET /admin/newsletter/campaigns */
    getCampaigns: async ({ page = 1, perPage = 20 } = {}) => {
        try {
            const { data } = await client.get('/admin/newsletter/campaigns', { params: { page, per_page: perPage } });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** GET /admin/newsletter/export — returns CSV blob URL */
    exportCsv: async () => {
        try {
            const response = await client.get('/admin/newsletter/export', { responseType: 'blob' });
            return response.data;  // Blob
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Trending ─────────────────────────────────────────────────────────────
export const trendingApi = {
    /** Fetch real-time trending topics from Google Trends */
    getTrending: async (region = 'united_states', platform = 'web') => {
        try {
            const { data } = await client.get(`/trending?region=${region}&platform=${platform}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ─── Scheduled Posts (Calendar) ─────────────────────────────────────────────
export const scheduledPostsApi = {
    /**
     * Fetch scheduled posts for a date range.
     * @param {string} start - ISO 8601 date string (inclusive)
     * @param {string} end   - ISO 8601 date string (inclusive)
     * @param {string} [platform] - Optional platform filter
     */
    getRange: async (start, end, platform) => {
        try {
            const params = { start, end };
            if (platform) params.platform = platform;
            const { data } = await client.get('/api/calendar/posts', { params });
            return data.posts || [];
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Get a single scheduled post by its Firestore document ID.
     * @param {string} postId
     */
    getById: async (postId) => {
        try {
            const { data } = await client.get(`/api/calendar/posts/${postId}`);
            return data.post;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Cancel (delete) a scheduled post.
     * @param {string} postId
     */
    cancel: async (postId) => {
        try {
            const { data } = await client.delete(`/api/calendar/posts/${postId}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Features Toggles ───────────────────────────────────────────────────────
export const featuresApi = {
    /** Fetch evaluated feature toggles for the current user */
    getFeatures: async () => {
        try {
            const { data } = await client.get('/features');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Fetch raw feature toggles config (Admin only) */
    getAdminToggles: async () => {
        try {
            const { data } = await client.get('/admin/toggles');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Update feature toggles config (Admin only) */
    updateAdminToggles: async (updateData) => {
        try {
            const { data } = await client.put('/admin/toggles', updateData);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Public — no auth required. Returns { waitlist_mode: true/false } */
    getWaitlistMode: async () => {
        try {
            const { data } = await client.get('/public/waitlist-mode');
            return data.waitlist_mode === true;
        } catch (err) {
            return false; // fail-open: if unreachable, show live site
        }
    },
};

// ── Admin Plan Management API ─────────────────────────────────────────────────
export const adminApi = {
    /** List all plans from Firestore (super_admin only) */
    listPlans: async () => {
        try {
            const { data } = await client.get('/admin/plans');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Create a new plan (super_admin only) */
    createPlan: async (planId, planData) => {
        try {
            const { data } = await client.post('/admin/plans', { plan_id: planId, ...planData });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Update an existing plan's fields (super_admin only) */
    updatePlan: async (planId, planData) => {
        try {
            const { data } = await client.put(`/admin/plans/${planId}`, planData);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Set display order for plans (super_admin only) */
    reorderPlans: async (orderedIds) => {
        try {
            const { data } = await client.put('/admin/plans/reorder', { order: orderedIds });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Soft-delete a plan — sets active=false (super_admin only) */
    deletePlan: async (planId) => {
        try {
            const { data } = await client.delete(`/admin/plans/${planId}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Assign a plan or role to a user (super_admin only) */
    updateUserRole: async (payload) => {
        try {
            const { data } = await client.post('/admin/roles', payload);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Search users by email or display name (super_admin only) */
    searchUsers: async (q) => {
        try {
            const { data } = await client.get('/admin/users/search', { params: { q } });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Set or clear a per-user custom quota override (super_admin only) */
    overrideUserQuota: async (uid, customQuotaLimit) => {
        try {
            const { data } = await client.post(`/admin/users/${uid}/quota`, {
                custom_quota_limit: customQuotaLimit,
            });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Grant persistent engagement top-up credits to a user (super_admin only) */
    grantEngagementTopup: async (uid, credits) => {
        try {
            const { data } = await client.post(`/admin/users/${uid}/engagement-topup`, { credits });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** List dead-lettered comment queue tasks (exhausted 3 retries) */
    getDeadLetterTasks: async (limit = 100) => {
        try {
            const { data } = await client.get('/admin/dead-letter', { params: { limit } });
            return data; // { tasks: [...], count: N }
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Re-queue a dead-letter task (resets attempts to 0) */
    retryDeadLetterTask: async (taskId) => {
        try {
            const { data } = await client.post(`/admin/dead-letter/${taskId}/retry`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Re-queue many dead-letter tasks in a single request. */
    retryDeadLetterBatch: async (taskIds) => {
        try {
            const { data } = await client.post('/admin/dead-letter/retry-batch', { task_ids: taskIds });
            return data; // { retried, failed, failed_ids }
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Permanently delete a dead-letter task */
    dismissDeadLetterTask: async (taskId) => {
        try {
            const { data } = await client.delete(`/admin/dead-letter/${taskId}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    // ── Infrastructure Switcher ─────────────────────────────────────────────

    /** Read full infra config + live platform detected on server */
    getInfraConfig: async () => {
        try {
            const { data } = await client.get('/admin/infra');
            return data; // { live_platform, config }
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Persist full or partial infra config (environment URLs, notes, etc.) */
    updateInfraConfig: async (payload) => {
        try {
            const { data } = await client.put('/admin/infra', payload);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Atomically flip the active_platform field */
    switchPlatform: async (platform) => {
        try {
            const { data } = await client.post('/admin/infra/platform', { platform });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Ping a URL and return { status_code, latency_ms, ok } */
    pingInfraUrl: async (url) => {
        try {
            const { data } = await client.post('/admin/infra/ping', { url });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Toggle waitlist mode on/off (super_admin only) */
    setWaitlistMode: async (state) => {
        try {
            const { data } = await client.post('/admin/toggles/waitlist', { state });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** List all active Stripe coupons (super_admin only) */
    listDiscounts: async () => {
        try {
            const { data } = await client.get('/admin/discounts');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Create a Stripe coupon (super_admin only) */
    createDiscount: async (payload) => {
        try {
            const { data } = await client.post('/admin/discounts', payload);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Deactivate a Stripe coupon (super_admin only) */
    deleteDiscount: async (couponId) => {
        try {
            const { data } = await client.delete(`/admin/discounts/${couponId}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Seed default plans into Firestore (super_admin only) */
    seedPlans: async () => {
        try {
            const { data } = await client.post('/admin/plans/seed');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Get R2 storage configuration (secret key masked) */
    getR2Config: async () => {
        try {
            const { data } = await client.get('/admin/r2-config');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Save R2 storage configuration to Firestore */
    updateR2Config: async (payload) => {
        try {
            const { data } = await client.put('/admin/r2-config', payload);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Test the current R2 connection */
    testR2Connection: async () => {
        try {
            const { data } = await client.post('/admin/r2-config/test');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Get Stripe + Razorpay gateway configuration (secrets masked) */
    getGatewayConfig: async () => {
        try {
            const { data } = await client.get('/admin/gateway-config');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Save Stripe / Razorpay settings to Firestore */
    updateGatewayConfig: async (payload) => {
        try {
            const { data } = await client.put('/admin/gateway-config', payload);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Test Stripe connectivity using the stored secret key */
    testStripeConnection: async () => {
        try {
            const { data } = await client.post('/admin/gateway-config/test-stripe');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Test Razorpay connectivity using the stored credentials */
    testRazorpayConnection: async () => {
        try {
            const { data } = await client.post('/admin/gateway-config/test-razorpay');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Get AI provider configuration (API keys masked) */
    getAIConfig: async () => {
        try {
            const { data } = await client.get('/admin/ai-config');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Save AI provider configuration to Firestore */
    updateAIConfig: async (payload) => {
        try {
            const { data } = await client.put('/admin/ai-config', payload);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Test the currently active AI provider connection */
    testAIConnection: async () => {
        try {
            const { data } = await client.post('/admin/ai-config/test');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** List all idea template niches */
    listIdeaNiches: async () => {
        try {
            const { data } = await client.get('/admin/idea-templates');
            return data;
        } catch (err) { throw createApiError(err); }
    },

    /** Get full templates + keywords for a niche */
    getIdeaNiche: async (niche) => {
        try {
            const { data } = await client.get(`/admin/idea-templates/${niche}`);
            return data;
        } catch (err) { throw createApiError(err); }
    },

    /** Update a niche (keywords + templates) */
    updateIdeaNiche: async (niche, nicheData) => {
        try {
            const { data } = await client.put(`/admin/idea-templates/${niche}`, nicheData);
            return data;
        } catch (err) { throw createApiError(err); }
    },

    /** Add a new niche */
    addIdeaNiche: async (niche, nicheData) => {
        try {
            const { data } = await client.post(`/admin/idea-templates/${niche}`, nicheData);
            return data;
        } catch (err) { throw createApiError(err); }
    },

    /** Delete a niche */
    deleteIdeaNiche: async (niche) => {
        try {
            const { data } = await client.delete(`/admin/idea-templates/${niche}`);
            return data;
        } catch (err) { throw createApiError(err); }
    },

    /** Trigger manual AI template refresh */
    triggerIdeaRefresh: async () => {
        try {
            const { data } = await client.post('/admin/idea-templates/ai-refresh');
            return data;
        } catch (err) { throw createApiError(err); }
    },

    /** Update template metadata (ai_refresh_enabled) */
    updateIdeaTemplateMeta: async (meta) => {
        try {
            const { data } = await client.put('/admin/idea-templates/meta', meta);
            return data;
        } catch (err) { throw createApiError(err); }
    },

    /** Get shared platform OAuth app credentials (secrets masked) */
    getPlatformConfig: async () => {
        try {
            const { data } = await client.get('/admin/platform-config');
            return data;
        } catch (err) { throw createApiError(err); }
    },

    /** Save shared platform OAuth app credentials */
    updatePlatformConfig: async (payload) => {
        try {
            const { data } = await client.put('/admin/platform-config', payload);
            return data;
        } catch (err) { throw createApiError(err); }
    },

    /** Test the stored Instagram App ID + Secret */
    testInstagramConnection: async () => {
        try {
            const { data } = await client.post('/admin/platform-config/test-instagram');
            return data;
        } catch (err) { throw createApiError(err); }
    },

    /** Test the stored Facebook App ID + Secret */
    testFacebookConnection: async () => {
        try {
            const { data } = await client.post('/admin/platform-config/test-facebook');
            return data;
        } catch (err) { throw createApiError(err); }
    },

    /** Validate the stored YouTube OAuth Client ID format */
    testYouTubeConnection: async () => {
        try {
            const { data } = await client.post('/admin/platform-config/test-youtube');
            return data;
        } catch (err) { throw createApiError(err); }
    },
};

// ── Imported / Synced Posts API ───────────────────────────────────────────────
export const importedPostsApi = {
    /** Trigger a sync for one platform — fetches & stores latest posts */
    sync: async (platform, accountId = null) => {
        try {
            const body = accountId ? { account_id: accountId } : {};
            const { data } = await client.post(`/api/posts/sync/${platform}`, body);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Get paginated imported posts, optionally filtered by platform */
    getAll: async (platform, page = 1, perPage = 12) => {
        try {
            const params = { page, per_page: perPage };
            if (platform) params.platform = platform;
            const { data } = await client.get('/api/posts/imported', { params });
            return data; // { posts, total, page, per_page, total_pages }
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Get a single post with its full growth history */
    getPost: async (postId) => {
        try {
            const { data } = await client.get(`/api/posts/imported/${postId}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Delete a post from the platform and remove it from our DB */
    deletePost: async (postId) => {
        try {
            const { data } = await client.delete(`/api/posts/imported/${postId}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Edit a post on the platform — body fields vary by platform */
    editPost: async (postId, updates) => {
        try {
            const { data } = await client.patch(`/api/posts/imported/${postId}`, updates);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Refresh engagement stats (likes, comments, views) for a single post from the platform API */
    refreshStats: async (postId) => {
        try {
            const { data } = await client.post(`/api/posts/imported/${postId}/refresh`);
            return data; // { stats, synced_at }
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** On-demand comment rescan for a single post — bypasses tier/backoff filters */
    rescanComments: async (postId) => {
        try {
            const { data } = await client.post(`/api/posts/imported/${postId}/rescan-comments`);
            return data; // { rescanned, platform, post_id }
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Get structured dashboard analytics from imported posts */
    getDashboard: async ({ platform, since, until } = {}) => {
        try {
            const params = {};
            if (platform) params.platform = platform;
            if (since)    params.since    = since;
            if (until)    params.until    = until;
            const { data } = await client.get('/api/posts/imported/dashboard', { params });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Blog API ────────────────────────────────────────────────────────────────
export const blogApi = {
    /** Public: get latest N published posts (for landing page) */
    latest: async () => {
        try {
            const { data } = await client.get('/api/blog/latest');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Admin: list all posts with optional status filter */
    list: async (params = {}) => {
        try {
            const { data } = await client.get('/api/blog/posts', { params });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Admin: get single post by slug */
    get: async (slug) => {
        try {
            const { data } = await client.get(`/api/blog/posts/${slug}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Admin: create a new post */
    create: async (postData) => {
        try {
            const { data } = await client.post('/api/blog/posts', postData);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Admin: update a post */
    update: async (slug, postData) => {
        try {
            const { data } = await client.put(`/api/blog/posts/${slug}`, postData);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Admin: delete a post */
    delete: async (slug) => {
        try {
            const { data } = await client.delete(`/api/blog/posts/${slug}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Admin: AI-generate article draft */
    generate: async (params) => {
        try {
            const { data } = await client.post('/api/blog/generate', params);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Admin: upload a cover image to R2, returns { url } */
    uploadImage: async (file) => {
        try {
            const formData = new FormData();
            formData.append('image', file);
            const { data } = await client.post('/api/blog/upload-image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 30_000,
            });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Admin: delete a cover image from R2 by its public URL */
    deleteImage: async (url) => {
        try {
            const { data } = await client.delete('/api/blog/image', { data: { url } });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Onboarding ────────────────────────────────────────────────────────────────
export const onboardingApi = {
    /** Save (full overwrite) onboarding questionnaire answers */
    save: async (onboardingData, completed = true) => {
        try {
            const { data } = await client.post('/onboarding', { onboarding: onboardingData, completed });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Retrieve current onboarding data and completion status */
    get: async () => {
        try {
            const { data } = await client.get('/onboarding');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Partially update onboarding answers */
    update: async (updates, completed) => {
        try {
            const payload = { onboarding: updates };
            if (completed !== undefined) payload.completed = completed;
            const { data } = await client.put('/onboarding', payload);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

export const invoicesApi = {
    /** List current user's invoices (newest first). Returns {invoices, has_more, total_returned}. */
    list: async (offset = 0, limit = 20) => {
        try {
            const { data } = await client.get('/api/invoices', { params: { offset, limit } });
            return data; // { invoices: [...], has_more: bool, total_returned: int }
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Get a fresh PDF URL for an invoice. Returns {pdf_url: string}. */
    getPdfUrl: async (invoiceId) => {
        try {
            const { data } = await client.get(`/api/invoices/${invoiceId}/pdf`);
            return data; // { pdf_url: '...' }
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Default export (convenience) ─────────────────────────────────────────────
const api = {
    analyticsApi,
    postApi,
    mediaApi,
    analyzeApi,
    gapsApi,
    rankApi,
    ideasApi,
    credentialsApi,
    billingApi,
    automationApi,
    trendingApi,
    newsletterApi,
    scheduledPostsApi,
    featuresApi,
    adminApi,
    importedPostsApi,
    blogApi,
    onboardingApi,
};

export default api;
